import React from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  CssVarsProvider,
  DialogContent,
  DialogTitle,
  extendTheme,
  Modal,
  ModalClose,
  ModalDialog,
  Snackbar,
  Stack,
  Typography,
} from '@mui/joy';
import { Route, Routes, useLocation } from 'react-router-dom';
import { getInitColorSchemeScript } from '@mui/joy/styles';
import Header from './components/Header';
import Chat from './pages/Chat';
import Main from './pages/Main';
import AuthProvider from './context/AuthContext';
import Settings from './pages/Settings';
import { useMediaQuery, useTheme } from '@mui/material';
import Explore from './pages/Explore';
import axios from 'axios';
import DynamicFaceCard from './components/DynamicFaceCard';
import Frame from './pages/Frame';
import Snowfall from 'react-snowfall';
import Income from './pages/Income';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { useLanguage } from './LanguageProvider';
import { Add } from '@mui/icons-material';
import Servers from './pages/Servers';
import Home from './pages/footage/Home';
import { getApiUrl } from './utils/api';
import Plans from './pages/Plans';
import Orders from './pages/Orders';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function App() {
  const themeSize = useTheme();
  const isMobile = useMediaQuery(themeSize.breakpoints.down('sm'));

  const apptheme = extendTheme({
    colorSchemes: {
      dark: {
        palette: {
          background: {
            surface: "hsla(0, 0%, 4%, 1)",
            body: '#0a0a0a',
          },
          header: {
            main: '#000',
          },
          primary: {
            main: '#6a1b9a',
          },
          text: {
            primary: '#ffffff',
          },
          neutral: {
            softBg: "hsla(0, 0%, 8%, 1)",
            softHoverBg: "hsla(0, 0%, 16%, 1)",
            softActiveBg: "hsla(0, 0%, 10%, 1)",
            softDisabledBg: "hsla(0, 0%, 8%, 0.6)",
          },
          warning: {
            softDisabledBg: "hsla(0, 0%, 8%, 0.6)",
          },
          message: {
            background: 'hsla(0, 0%, 10%, 1)'
          },
          progline: "#65487a",
          add: {
            def: "#6a1b9a99",
            hover: "#6a1b9a",
          },
          like: "#72430D",
          codeBtn: "hsla(0, 0%, 6%, 1)",
          code: "hsla(0, 0%, 8%, 1)",
          codeOut: "hsla(0, 0%, 6%, 1)",
          highlight: 'hsla(0, 0%, 20%, 1)',
          highlightText: "#CDD7E1",
        },
      },
      light: {
        palette: {
          background: {
            surface: "#fff",
            body: '#f7f7fa',
          },
          header: {
            main: '#fff',
          },
          primary: {
            main: '#8e24aa',
          },
          text: {
            primary: '#000',
          },
          neutral: {
            softBg: "#f3eaff",
            softHoverBg: "#e6d6fa",
            softActiveBg: "#f3eaff99",
            softDisabledBg: "#f3eaff99",
          },
          warning: {
            softDisabledBg: "#f3eaff99",
          },
          message: {
            background: '#f8f2ff'
          },
          progline: "#d5b8ff",
          add: {
            def: "#d5b8ff99",
            hover: "#d5b8ff",
          },
          like: "#ffda9e",
          codeBtn: "#fff",
          code: "#f3eaff",
          codeOut: "#d5b8ff",
          highlight: "rgba(255, 165, 252, 0.5)",
          highlightText: "#000",
        },
      },
    },
    components: {
      JoyInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 50,
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'var(--joy-palette-text-primary, #222)',
            borderColor: 'var(--joy-palette-primary-main, #8e24aa)',
            background: 'var(--joy-palette-background-surface, #fff)',
            transition: 'border-color 0.3s',
            '--Input-focusedHighlight': 'rgba(0,0,0,.0)',
            '--Input-focusedThickness': '0rem',
            '&:focus': {
              outline: 'none',
            },
            '&.Mui-focused': {
              outline: 'none',
            },
            '&::before, &::after': {
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              zIndex: '-1',
              width: 'calc(100% + 10px)',
              height: 'calc(100% + 10px)',
              background: theme.palette.mode === 'dark' 
                ? `linear-gradient(
                    45deg,
                    #ff005599,
                    #a200ff99,
                    #ff005599
                  )`
                : `linear-gradient(
                    45deg,
                    #e1bee7cc,
                    #f3eaffcc,
                    #e1bee7cc
                  )`,
              backgroundSize: '300%',
              animation: isMobile ? '' : 'border 18s ease infinite',
              filter: 'blur(20px)',
              '-webkit-filter': 'blur(20px)',
              '-moz-filter': 'blur(20px)',
              '-ms-filter': 'blur(20px)',
            },
            '&.MuiInputBase-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#6a1b9a' : '#8e24aa',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#7b1fa2' : '#6a1b9a',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#8e24aa' : '#7b1fa2',
              },
            },
            '&.Mui-disabled': {
              borderColor: theme.palette.mode === 'dark' ? '#4a0b6a' : '#e1bee7',
            },
          }),
        },
      },
      JoyTextarea: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 50,
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'var(--joy-palette-text-primary, #222)',
            borderColor: 'var(--joy-palette-primary-main, #8e24aa)',
            background: 'var(--joy-palette-background-surface, #fff)',
            transition: 'border-color 0.3s',
            '--Textarea-focusedHighlight': 'rgba(0,0,0,.0)',
            '--Textarea-focusedThickness': '0rem',
            '&:focus': {
              outline: 'none',
            },
            '&.Mui-focused': {
              outline: 'none',
            },
            '&::before, &::after': {
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              zIndex: '-1',
              width: 'calc(100% + 10px)',
              height: 'calc(100% + 10px)',
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(
                    45deg,
                    #ff005599,
                    #a200ff99,
                    #ff005599
                  )`
                : `linear-gradient(
                    45deg,
                    #e1bee7cc,
                    #f3eaffcc,
                    #e1bee7cc
                  )`,
              backgroundSize: '300%',
              animation: isMobile ? '' : 'border 18s ease infinite',
              filter: 'blur(20px)',
              '-webkit-filter': 'blur(20px)',
              '-moz-filter': 'blur(20px)',
              '-ms-filter': 'blur(20px)',
            },
            '&.MuiInputBase-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#6a1b9a' : '#8e24aa',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#7b1fa2' : '#6a1b9a',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#8e24aa' : '#7b1fa2',
              },
            },
            '&.Mui-disabled': {
              borderColor: theme.palette.mode === 'dark' ? '#4a0b6a' : '#e1bee7',
            },
          }),
          textarea: {
            marginRight: 58,
            marginLeft: 34,
            marginTop: -38,
            marginBottom: 6,
            scrollbarWidth: 'none',
          },
        },
      },
      JoyAutocomplete: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 50,
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'var(--joy-palette-text-primary, #222)',
            borderColor: 'var(--joy-palette-primary-main, #8e24aa)',
            background: 'var(--joy-palette-background-surface, #fff)',
            transition: 'border-color 0.3s',
            '--Textarea-focusedHighlight': 'rgba(0,0,0,.0)',
            '--Textarea-focusedThickness': '0rem',
            '&:focus': {
              outline: 'none',
            },
            '&.Mui-focused': {
              outline: 'none',
            },
            '&::before, &::after': {
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              zIndex: '-1',
              width: 'calc(100% + 10px)',
              height: 'calc(100% + 10px)',
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(
                    45deg,
                    #ff005599,
                    #a200ff99,
                    #ff005599
                  )`
                : `linear-gradient(
                    45deg,
                    #e1bee7cc,
                    #f3eaffcc,
                    #e1bee7cc
                  )`,
              backgroundSize: '300%',
              animation: isMobile ? '' : 'border 18s ease infinite',
              filter: 'blur(20px)',
              '-webkit-filter': 'blur(20px)',
              '-moz-filter': 'blur(20px)',
              '-ms-filter': 'blur(20px)',
            },
            '&.MuiInputBase-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#6a1b9a' : '#8e24aa',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#7b1fa2' : '#6a1b9a',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#8e24aa' : '#7b1fa2',
              },
            },
            '&.Mui-disabled': {
              borderColor: theme.palette.mode === 'dark' ? '#4a0b6a' : '#e1bee7',
            },
          }),
          textarea: {
            marginRight: 58,
            marginLeft: 34,
            marginTop: -38,
            marginBottom: 6,
            scrollbarWidth: 'none',
          },
        },
      },
      JoySelect: {
        styleOverrides: {
          root: {
            autoFocus: false,
          },
          listbox: {
            zIndex: 99999,
          }
        }
      },
    },
  });

  const query = useQuery();

  const location = useLocation();

  const [version, setVersion] = React.useState(() => {
    return localStorage.getItem('version') || '4';
  });

  const [temperature, setTemperature] = React.useState(() => {
    return localStorage.getItem('temperature') || 0.7;
  });

  const [voice, setVoice] = React.useState(() => {
    return localStorage.getItem('voice') || 'alloy';
  });

  const [systemInstructions, setSystemInstructions] = React.useState(() => {
    return localStorage.getItem('systemInstructions') || "";
  });

  React.useEffect(() => {
    localStorage.setItem('version', version);
  }, [version]);

  React.useEffect(() => {
    localStorage.setItem('temperature', temperature);
  }, [temperature]);

  React.useEffect(() => {
    localStorage.setItem('systemInstructions', systemInstructions);
  }, [systemInstructions]);

  React.useEffect(() => {
    localStorage.setItem('voice', voice);
  }, [voice]);

  const [newsData, setNewsData] = React.useState(null);
  const [newsOpen, setNewsOpen] = React.useState(false);
  const [cookiesOpen, setCookiesOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(getApiUrl('/v3/ai/news'));
        setNewsOpen((localStorage.getItem('news') != response.data?.id) && response.data?.released);
        setNewsData(response.data);
      } catch { }
    };

    const showCookies = () => {
      setCookiesOpen(!localStorage.getItem('cookies'));
    }

    showCookies();
    fetchData();
  }, []);

  const handleNewsClose = () => {
    localStorage.setItem('news', newsData.id);
    setNewsOpen(false);
  }

  const handleCookiesClose = () => {
    localStorage.setItem('cookies', true);
    setCookiesOpen(false);
  }

  const model = query.get('model');
  const [customModel, setCustomModel] = React.useState(null);

  React.useEffect(() => {
    if (model) {
      const fetchModelData = async () => {
        try {
          const response = await fetch(getApiUrl(`/v3/ai/model/${model}`));
          if (!response.ok) {
            throw new Error('Failed to fetch model data');
          }
          const data = await response.json();
          setCustomModel(data);
        } catch (error) {
          setCustomModel(null);
        }
      };
      fetchModelData();
    } else {
      setCustomModel(null);
    }
  }, [model, version]);

  const [isFaceVisible, setIsFaceVisible] = React.useState(false);

  const isHolidaySeason = () => {
    const today = new Date();
    return (
      false
    );
  };

  return (
    <>
      {getInitColorSchemeScript()}
      <div id='body-ROOT' style={{ 
        backgroundColor: 'var(--joy-palette-background-body)',
        width: '100vw', 
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        transition: 'background-color 0.3s ease'
      }} />
      <CssVarsProvider theme={apptheme}>
        <AuthProvider customLoad={model && !customModel}>
          <Header openNews={setNewsOpen} CapricornVersion={version} onChangeCapricornVersion={setVersion} currentPage={location.pathname} customModel={customModel} />
          {isHolidaySeason() && (
            <Snowfall snowflakeCount={200} style={{ position: 'fixed', opacity: 0.7, zIndex: -1, pointerEvents: 'none', top: 0, left: 0, right: 0, bottom: 0 }} />
          )}

          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/chat" element={<Chat voiceName={voice} version={version} temperature={temperature} systemInstructions={systemInstructions} customModel={customModel} />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/explore/search" element={<Explore />} />
            <Route path="/test" element={<Frame />} />
            <Route path="/settings" element={<Settings isMobile={isMobile} faceSet={setIsFaceVisible} temperature={temperature} onChangeTemperature={setTemperature} voice={voice} setVoice={setVoice} instructions={systemInstructions} onSetInstructions={setSystemInstructions} />} />
            <Route path="/income" element={<Income />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/servers" element={<Servers />} />
            <Route path="/plans" element={<Plans />} />
          </Routes>

          <Modal onClose={handleNewsClose} open={newsOpen}>
            <ModalDialog sx={{ borderRadius: 20, maxHeight: '60%', backgroundColor: 'var(--joy-palette-background-surface)' }} variant="plain">
              <ModalClose sx={{ borderRadius: 250 }} />
              <DialogTitle>{newsData?.title}</DialogTitle>
              <DialogContent sx={{ scrollbarWidth: 'none' }}>
                <div style={{ marginTop: -10 }} dangerouslySetInnerHTML={{ __html: newsData?.html }} />
              </DialogContent>
            </ModalDialog>
          </Modal>

          {!window.__TAURI_INTERNALS__ && (
            <Snackbar
              variant="outlined"
              color="primary"
              size="lg"
              invertedColors
              open={cookiesOpen}
              onClose={() => setCookiesOpen(false)}
              anchorOrigin={{ vertical: isMobile ? 'top' : 'bottom', horizontal: isMobile ? 'center' : 'right' }}
              sx={(theme) => ({
                zIndex: 999999999,
                maxWidth: 360,
                borderRadius: 20
              })}
            >
              <Box>
                <Typography level="title-lg">We use cookies 🍪</Typography>
                <Typography sx={{ mt: 1, mb: 2 }}>
                  Capricorn AI uses cookies to personalize content and analyse traffic in order to offer you a better experience.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button sx={{ borderRadius: 250 }} variant="soft" color="primary" onClick={() => handleCookiesClose()}>
                    I accept
                  </Button>
                  <Button
                    sx={{ borderRadius: 250 }}
                    variant="soft" color="neutral"
                    onClick={() => handleCookiesClose()}
                  >
                    Reject
                  </Button>
                </Stack>
              </Box>
            </Snackbar>
          )}
        </AuthProvider>
        <SpeedInsights />
        <Analytics />
      </CssVarsProvider>
    </>
  );
}

export default App;
